import { QueryResult, useMutation, useQuery } from '@apollo/client';
import { ContentLoader } from '@saucelabs/sl-component-loader';
import React from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import { graphql, useFragment } from '../../graphql/__generated__';
import { DiffsOrderBy, ReadBuildDocumentQuery, UpdateDiffStatus } from '../../graphql/__generated__/graphql';
import { VisualBuildDetailsPageParams, urls } from '../../routes';
import { ContentErrorHandler } from '../common/pages/ContentErrorHandler';
import { BuildFrag, filterDiffNotQueued } from '../VisualBuildDetailsPage';
import DiffView from './DiffView';
import { diffSortKey } from './DiffHeaderSort';

export const DiffFrag = graphql(`
  fragment DiffFrag on Diff {
    id
    ...DiffViewFragment
    ...DiffStageMetaFragment
  }
`);

const ReadDiffDocument = graphql(/* GraphQL */ `
  query ReadDiffDocument($id: UUID!) {
    diff(id: $id) {
      ...DiffFrag
    }
  }
`);

const UpdateDiffMutation = graphql(/* GraphQL */ `
  mutation UpdateDiffMutation($id: ID!, $status: UpdateDiffStatus!, $order: [DiffsOrderBy!], $filter: DiffFilter!) {
      updateDiff(input: { id: $id, status: $status }) {
          ...DiffStageMetaFragment
          build {
            ...BuildFrag
          }
      }
  }
`);

type DiffProps = {
  buildResult: QueryResult<ReadBuildDocumentQuery, {
    id: string;
    order: any;
  }>,
  order: DiffsOrderBy
}

const Diff: React.FC<DiffProps> = ({ buildResult, order }) => {
  const build = useFragment(BuildFrag, buildResult.data?.build);
  const { buildId, stepId } = useParams<VisualBuildDetailsPageParams>();
  const history = useHistory();
  const diffResult = useQuery(ReadDiffDocument, {
    variables: { id: stepId },
  });

  const diff = useFragment(DiffFrag, diffResult.data?.diff);

  const [updateDiff] = useMutation(UpdateDiffMutation);

  const accept = async () => {
    await updateDiff({
      variables: {
        id: diff?.id,
        order,
        status: UpdateDiffStatus.Approved,
        filter: filterDiffNotQueued,
      },
    });
  };

  const reject = async () => {
    await updateDiff({
      variables: {
        id: diff?.id,
        order,
        status: UpdateDiffStatus.Rejected,
        filter: filterDiffNotQueued,
      },
    });
  };

  const unapprove = async () => {
    await updateDiff({
      variables: {
        id: diff?.id,
        order,
        status: UpdateDiffStatus.Unapproved,
        filter: filterDiffNotQueued,
      },
    });
  };

  const setDiffsOrder = (value: DiffsOrderBy) => {
    localStorage.setItem(diffSortKey, value);
    history.push(urls.getDiff(buildId, stepId, { order: value }));
  };

  if (!stepId && diffResult.data && diff?.id) {
    return (
      <Redirect
        to={urls.getDiff(buildId, diff.id)}
      />
    );
  }

  return (
    <>
      <ContentLoader
        show={diffResult.loading && !diffResult.data}
      >
        <ContentErrorHandler
          isNotFoundError={!build}
          isServerError={!diff}
        >
          {diff && (
            <div className="flex-1 min-h-0">
              <DiffView
                build={build!}
                diff={diff}
                diffOrder={order}
                onAccept={accept}
                onReject={reject}
                onUnapprove={unapprove}
                setDiffsOrder={setDiffsOrder}
              />
            </div>
          )}
        </ContentErrorHandler>
      </ContentLoader>
    </>
  );
};

export default Diff;
